import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { type Coupon as CouponModel, find } from '@/entities/coupon';
import { NO_IMAGE, safeSrc } from '@/shared/lib/image';
import { isHttpUrl } from '@/shared/lib/url';
import { useAsync } from '@/shared/lib/use-async';
import { Button } from '@/shared/ui/button/button';
import { Guidance } from '@/shared/ui/guidance/guidance';
import { useToast } from '@/shared/ui/toast/toast';
import { LinkedText } from './linked-text';
import './coupon.css';

export function Coupon() {
  const { code = '' } = useParams();
  const { data, loading, error, reload } = useAsync(() => find(code), [code]);

  const coupon = data?.status === 'ok' ? data.coupon : null;
  const externalUrl = coupon?.numberType === 'URL' && isHttpUrl(coupon.number) ? coupon.number : null;
  const unusable = coupon !== null && coupon.numberType !== 'BARCODE' && externalUrl === null;

  useEffect(() => {
    if (externalUrl) window.location.replace(externalUrl);
  }, [externalUrl]);

  return (
    <div className="coupon-view" aria-live="polite">
      {loading ? <p className="coupon-view__loading">쿠폰을 확인하는 중이에요…</p> : null}

      {!loading && error ? (
        <Guidance
          message="쿠폰을 불러오지 못했어요"
          hint="네트워크 상태를 확인한 뒤 다시 시도해주세요"
          action={<Button onClick={reload}>다시 시도</Button>}
        />
      ) : null}

      {data?.status === 'error' || unusable ? (
        <Guidance
          message={data?.status === 'error' && data.show ? data.message : '쿠폰을 확인할 수 없어요'}
          hint="전달받은 쿠폰 링크를 다시 확인해주세요"
        />
      ) : null}

      {data?.status === 'unsupported' ? (
        <Guidance message="이 링크는 여기서 열 수 없어요" hint="쿠폰을 보낸 곳에서 받은 링크로 다시 열어주세요" />
      ) : null}

      {externalUrl ? (
        <Guidance
          message="다른 페이지로 이동할게요"
          hint="자동으로 넘어가지 않으면 아래 버튼을 눌러주세요"
          action={
            <a className="btn btn--primary" href={externalUrl} rel="noopener noreferrer">
              쿠폰 열기
            </a>
          }
        />
      ) : null}

      {coupon && coupon.numberType === 'BARCODE' ? <CouponCard coupon={coupon} /> : null}
    </div>
  );
}

function CouponCard({ coupon }: { coupon: CouponModel }) {
  const toast = useToast();
  const captureRef = useRef<HTMLDivElement>(null);
  const barcodeRef = useRef<HTMLCanvasElement>(null);
  const [saving, setSaving] = useState(false);
  const [proxyFailed, setProxyFailed] = useState(false);
  const [corsFailedSrc, setCorsFailedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [copiedAt, setCopiedAt] = useState<number | null>(null);

  useEffect(() => {
    if (copiedAt === null) return;
    const timer = setTimeout(() => setCopiedAt(null), 1600);
    return () => clearTimeout(timer);
  }, [copiedAt]);

  const proxy = proxyFailed ? null : coupon.imageProxy;
  const proxied = proxy !== null;
  const src = proxy ?? safeSrc(coupon.image);
  const imageSrc = failedSrc === src ? NO_IMAGE : src;
  const cors = corsFailedSrc !== src;
  const chunked = coupon.number.match(/.{1,4}/g)?.join(' ') ?? coupon.number;

  useEffect(() => {
    let cancelled = false;

    async function draw() {
      const { default: JsBarcode } = await import('jsbarcode');
      if (cancelled || !barcodeRef.current) return;
      const scale = window.devicePixelRatio || 1;
      JsBarcode(barcodeRef.current, coupon.number, {
        format: 'CODE128',
        width: 3 * scale,
        height: 80 * scale,
        margin: 10 * scale,
        displayValue: false,
        background: '#ffffff',
        lineColor: '#000000',
      });
    }

    draw();

    return () => {
      cancelled = true;
    };
  }, [coupon.number]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(coupon.number);
      setCopiedAt(Date.now());
    } catch {
      toast.show({
        title: '복사에 실패했어요',
        description: '화면의 쿠폰번호를 보고 직접 입력해주세요.',
      });
    }
  }

  async function handleSave() {
    if (saving || !captureRef.current) return;
    setSaving(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(captureRef.current, {
        useCORS: true,
        allowTaint: true,
        onclone: (doc) => {
          for (const el of doc.querySelectorAll<HTMLElement>('.coupon-card__copy, .coupon-card__copied')) {
            el.style.visibility = 'hidden';
          }
        },
      });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${coupon.name}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.show({ title: '이미지가 저장됐어요' });
    } catch {
      toast.show({
        title: '이미지 저장에 실패했어요',
        description: '잠시 후 다시 시도해주세요.',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <section className="coupon-page">
        <div className="coupon-page__inner">
          <div className="coupon-card" ref={captureRef}>
            <figure className="coupon-card__image">
              <img
                src={imageSrc}
                alt={`${coupon.brand} ${coupon.name}`}
                crossOrigin={cors ? 'anonymous' : undefined}
                onError={() => {
                  if (proxied) setProxyFailed(true);
                  else if (cors) setCorsFailedSrc(src);
                  else setFailedSrc(src);
                }}
              />
            </figure>

            <div className="coupon-card__body">
              <p className="coupon-card__brand">{coupon.brand}</p>
              <p className="coupon-card__name">{coupon.name}</p>

              <dl className="coupon-card__meta">
                <div className="coupon-card__meta-row">
                  <dt>교환처</dt>
                  <dd>{coupon.brand}</dd>
                </div>
                <div className="coupon-card__meta-row">
                  <dt>유효기간</dt>
                  <dd>{coupon.expired}</dd>
                </div>
              </dl>

              <div className="coupon-card__barcode">
                <canvas ref={barcodeRef} role="img" aria-label={`바코드 ${coupon.number}`} />
                <div className="coupon-card__number">
                  <span>{chunked}</span>
                  <button type="button" className="coupon-card__copy" aria-label="쿠폰번호 복사" onClick={handleCopy}>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                      <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                      <path d="M15 5H6a1 1 0 0 0-1 1v9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                  <span className="coupon-card__copied" role="status">
                    {copiedAt !== null ? '쿠폰번호를 복사했어요' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <Button className="coupon-save-btn" onClick={handleSave} disabled={saving} aria-busy={saving}>
            {saving ? '저장 중…' : '교환쿠폰 저장'}
          </Button>

          <div className="coupon-notice">
            <h2>이미지 저장 경로 안내</h2>
            <p>안드로이드(AOS): '갤러리' 앱</p>
            <p>아이폰(iOS): '파일' 앱 &gt; '다운로드' 폴더</p>
          </div>
        </div>
      </section>

      <section className="coupon-page-desc safe-bottom">
        <div className="coupon-page__inner">
          <div className="coupon-notice">
            <h2>이용안내</h2>

            <div className="coupon-notice__group">
              <h3>사용가능매장</h3>
              {coupon.store.map((line) => (
                <LinkedText key={line} text={line} />
              ))}
            </div>

            <div className="coupon-notice__group">
              <h3>유의사항</h3>
              {coupon.precaution.map((line) => (
                <LinkedText key={line} text={line} />
              ))}
            </div>

            <div className="coupon-notice__group">
              <h3>제한사항</h3>
              {coupon.restriction.map((line) => (
                <LinkedText key={line} text={line} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
